'use client'

import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import { trackContactFormComplete } from '@/lib/tracking'
import { Input } from '@/app/[locale]/(ui)/input'
import { Textarea } from '@/app/[locale]/(ui)/textarea'
import { Button } from '@/app/[locale]/(ui)/button'
import { useTranslations } from 'next-intl'
import { ContactForm, ContactFormSchema } from '@/types/contact-form'
import { sendContactForm } from '@/lib/contact-form'
import { Alert } from '@/app/[locale]/(ui)/alert'

export const ContactUsForm = () => {
  const t = useTranslations()
  const [hasSubmitCompleted, setHasSubmitCompleted] = useState(false)
  const [showError, setShowError] = useState(false)
  const contactUsSchema = ContactFormSchema(t)

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ContactForm>({
    resolver: zodResolver(contactUsSchema),
    defaultValues: { phoneNumber: '', name: '', email: '', subject: '', message: '' },
  })

  const onSubmit = async (form: ContactForm) => {
    if (hasSubmitCompleted) return

    try {
      await sendContactForm(form)
    } catch {
      setShowError(true)
    }
    reset()
    trackContactFormComplete()
    setHasSubmitCompleted(true)
  }

  return (
    <div className="grid w-full grid-cols-4">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="col-span-4 grid grid-cols-2 gap-4 md:col-span-3 md:gap-6"
        noValidate
        autoComplete="off"
      >
        <Controller
          control={control}
          name="name"
          render={({ field }) => (
            <Input
              id="name"
              label={t('Name')}
              value={field.value}
              onChange={field.onChange}
              className="col-span-2 sm:col-span-1"
              error={errors.name?.message}
              required
            />
          )}
        />
        <Controller
          control={control}
          name="email"
          render={({ field }) => (
            <Input
              id="email"
              label={t('Email')}
              value={field.value}
              onChange={field.onChange}
              className="col-span-2 sm:col-span-1"
              error={errors.email?.message}
              required
            />
          )}
        />
        <Controller
          control={control}
          name="phoneNumber"
          render={({ field }) => (
            <Input
              id="phone"
              inputMode="numeric"
              label={t('Phone_number')}
              value={field.value}
              onChange={field.onChange}
              className="col-span-2 sm:col-span-1"
              error={errors.phoneNumber?.message}
              required
            />
          )}
        />
        <Controller
          control={control}
          name="subject"
          render={({ field }) => (
            <Input
              id="subject"
              label={t('Subject')}
              value={field.value}
              onChange={field.onChange}
              className="col-span-2 sm:col-span-1"
              error={errors.subject?.message}
              required
            />
          )}
        />
        <Controller
          control={control}
          name="message"
          render={({ field }) => (
            <Textarea
              id="message"
              label={t('Message')}
              value={field.value}
              onChange={field.onChange}
              className="col-span-2"
              placeholder={t('How_can_we_help')}
              error={errors.message?.message}
              required
              rows={8}
            />
          )}
        />
        <div className="col-span-2 flex justify-end max-sm:pt-2">
          <Button variant="primary" className="w-full sm:w-auto" type="submit" disabled={isSubmitting}>
            {t('Submit')}
          </Button>
        </div>
        {hasSubmitCompleted && !showError && <Alert variant="success">{t('Contact_form_success')}</Alert>}
        {hasSubmitCompleted && showError && <Alert variant="error">{t('Contact_form_error')}</Alert>}
      </form>
    </div>
  )
}
